// import React, { useState, useEffect, useRef } from "react";
// import { ref, onValue } from "firebase/database";
// import { database as realtimeDB } from "../FirebaseConfig";
// import Lottie from "lottie-react";
// import { motion, AnimatePresence } from "framer-motion";
// import noReviewsAnimation from "./no-reviews.json"; // Ensure this file exists

// const Reviews = ({ id }) => {
//   const [reviews, setReviews] = useState([]);
//   const [overallRating, setOverallRating] = useState(0);
//   const [reviewCount, setReviewCount] = useState(0);
//   const [loading, setLoading] = useState(true);
//   const scrollRef = useRef(null);
//   const isMobile = window.innerWidth < 768; // Check if mobile

//   useEffect(() => {
//     if (!id) return;

//     const reviewsRef = ref(realtimeDB, `reviews/${id}`);

//     const unsubscribe = onValue(reviewsRef, (snapshot) => {
//       if (snapshot.exists()) {
//         let allReviews = Object.values(snapshot.val() || {});
//         setReviewCount(allReviews.length);

//         if (allReviews.length > 0) {
//           const totalRating = allReviews.reduce((acc, rev) => acc + rev.rating, 0);
//           setOverallRating((totalRating / allReviews.length).toFixed(1));
//         }

//         let shuffled = allReviews.sort(() => 0.5 - Math.random());
//         setReviews([...shuffled, ...shuffled]); // Duplicate for infinite scrolling
//       } else {
//         setReviews([]);
//         setReviewCount(0);
//         setOverallRating(0);
//       }
//       setLoading(false);
//     });

//     return () => unsubscribe();
//   }, [id]);

//   return (
//     <div className="mt-16 bg-white p-6 md:p-8 rounded-xl shadow-lg border border-gray-200">
//       {/* Overall Rating Summary */}
//       <div className="flex flex-col md:flex-row justify-between items-center mb-6 md:mb-8">
//         <div className="text-center md:text-left">
//           <h2 className="text-2xl md:text-3xl font-extrabold text-gray-800">Customer Reviews</h2>
//           {reviewCount > 0 ? (
//             <p className="text-gray-600 mt-1 md:mt-2 text-md md:text-lg">
//               ⭐ <span className="text-lg md:text-xl font-bold">{overallRating}</span> out of 5 stars ({reviewCount} reviews)
//             </p>
//           ) : (
//             <p className="text-gray-500 mt-1 md:mt-2 text-sm md:text-md">No ratings yet. Be the first to review! 🌟</p>
//           )}
//         </div>
//       </div>

//       {/* No Reviews Message */}
//       {loading ? (
//         <div className="flex justify-center items-center h-40">
//           <p className="text-gray-500 text-lg">Loading reviews...</p>
//         </div>
//       ) : reviewCount === 0 ? (
//         <div className="flex flex-col justify-center items-center">
//           <Lottie animationData={noReviewsAnimation} loop autoPlay style={{ width: "150px", md: "200px" }} />
//           <p className="text-gray-600 mt-4 text-md md:text-lg italic">
//             No reviews yet! Be the first to share your experience. 😊
//           </p>
//         </div>
//       ) : (
//         // Framer Motion Scroll Section with AnimatePresence
//         <motion.div
//           ref={scrollRef}
//           className={`relative overflow-hidden ${isMobile ? "h-[100px]" : "w-full"}`}
//           whileHover={{ pause: true }} // Pause animation on hover in desktop view
//         >
//           <AnimatePresence>
//             <motion.div
//               className={`flex ${isMobile ? "flex-col space-y-4" : "flex-row space-x-6"} p-4`}
//               animate={{ x: isMobile ? 0 : [-100, -200, -300, -400, -500, -600, -700, -800, -100], y: isMobile ? [-100, -200, -300, -400, -500] : 0 }}
//               transition={{
//                 repeat: Infinity,
//                 repeatType: "mirror",
//                 duration: 20, // Adjust speed
//                 ease: "linear",
//               }}
//             >
//               {reviews.map((review, index) => (
//                 <motion.div
//                   key={index}
//                   className="bg-white/80 backdrop-blur-lg p-6 min-w-[250px] sm:min-w-[300px] md:min-w-[350px] lg:min-w-[400px] rounded-xl shadow-xl hover:shadow-2xl transition-transform transform hover:-translate-y-2 duration-300 border border-gray-300"
//                   whileHover={{ scale: 1.05 }} // Slight scale effect on hover
//                   initial={{ opacity: 0, y: isMobile ? 30 : 0, x: isMobile ? 0 : 30 }}
//                   animate={{ opacity: 1, y: 0, x: 0 }}
//                   exit={{ opacity: 0, y: isMobile ? -30 : 0, x: isMobile ? 0 : -30 }}
//                   transition={{ duration: 0.5 }}
//                 >
//                   {/* Name Tag */}
//                   <div className="mb-2 px-3 py-1 inline-block bg-gradient-to-r from-orange-500 to-yellow-400 text-white font-semibold text-sm rounded-full shadow-md">
//                     {review.name}
//                   </div>

//                   {/* Rating Stars */}
//                   <div className="flex mt-2 space-x-1">
//                     {Array.from({ length: review.rating }).map((_, i) => (
//                       <span key={i} className="text-yellow-400 text-xl">★</span>
//                     ))}
//                     {Array.from({ length: 5 - review.rating }).map((_, i) => (
//                       <span key={i} className="text-gray-300 text-xl">★</span>
//                     ))}
//                   </div>

//                   {/* Review Content */}
//                   <p className="mt-2 text-gray-700 leading-relaxed italic text-sm md:text-md">
//                     “{review.review}”
//                   </p>
//                 </motion.div>
//               ))}
//             </motion.div>
//           </AnimatePresence>
//         </motion.div>
//       )}
//     </div>
//   );
// };

// export default Reviews;





import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

const Reviews = ({ reviews = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // 1. Calculate Stats & Sort Data
  const { averageRating, sortedReviews, topReviews } = useMemo(() => {
    const total = reviews.length;
    if (total === 0) return { averageRating: 0, sortedReviews: [], topReviews: [] };

    const sum = reviews.reduce((acc, curr) => acc + (Number(curr.rating) || 0), 0);
    const avg = (sum / total).toFixed(1);

    // Sort by rating (High to Low)
    const sorted = [...reviews].sort((a, b) => b.rating - a.rating);
    
    // Get Top 5
    const top5 = sorted.slice(0, 5);

    return { averageRating: avg, sortedReviews: sorted, topReviews: top5 };
  }, [reviews]);

  // If no reviews, don't render anything or render a placeholder
  if (!reviews || reviews.length === 0) {
    return (
      <div className="bg-orange-50/50 rounded-xl p-6 text-center border border-orange-100">
        <p className="text-gray-500 italic">No reviews yet. Be the first to try it!</p>
      </div>
    );
  }

  const reviewsToDisplay = isExpanded ? sortedReviews : topReviews;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-8 relative overflow-hidden">
      
      {/* --- Header Section --- */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            Customer Reviews
            <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {reviews.length}
            </span>
          </h2>
          
          <div className="flex items-center gap-2 mt-2">
            <span className="text-3xl font-extrabold text-gray-900">{averageRating}</span>
            <div className="flex flex-col">
              <div className="flex text-yellow-400 text-lg">
                {[...Array(5)].map((_, i) => (
                  <span key={i}>
                    {i < Math.round(averageRating) ? "★" : <span className="text-gray-300">★</span>}
                  </span>
                ))}
              </div>
              <span className="text-xs text-gray-500">Average Rating</span>
            </div>
          </div>
        </div>

        {/* Expand/Collapse Button */}
        {reviews.length > 5 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm font-semibold text-orange-600 hover:text-orange-700 underline decoration-dotted underline-offset-4 transition"
          >
            {isExpanded ? "Show Top 5 Only" : `See All ${reviews.length} Reviews`}
          </button>
        )}
      </div>

      {/* --- Reviews List --- */}
      <div className={`
        grid gap-4 transition-all duration-500 ease-in-out
        ${isExpanded 
          ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" // Grid when expanded
          : "grid-flow-col auto-cols-[85%] md:auto-cols-[40%] lg:auto-cols-[30%] overflow-x-auto pb-4 snap-x snap-mandatory hide-scrollbar" // Horizontal scroll when collapsed
        }
      `}>
        <AnimatePresence>
          {reviewsToDisplay.map((review, index) => (
            <ReviewCard key={index} review={review} index={index} />
          ))}
        </AnimatePresence>
      </div>

    </div>
  );
};

// --- Individual Card Component ---
const ReviewCard = ({ review, index }) => {
  // Generate a random pastel color for the avatar background based on name length
  const colors = ["bg-red-100", "bg-green-100", "bg-blue-100", "bg-yellow-100", "bg-purple-100", "bg-pink-100"];
  const colorClass = colors[review.name.length % colors.length];
  const textColorClass = colorClass.replace("bg-", "text-").replace("100", "700");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="snap-center bg-gray-50 border border-gray-100 p-4 rounded-xl hover:shadow-md transition-shadow duration-300 flex flex-col h-full min-h-[160px]"
    >
      {/* User Info */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${colorClass} ${textColorClass}`}>
          {review.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
          <div className="flex text-yellow-400 text-xs">
            {[...Array(5)].map((_, i) => (
               <span key={i}>{i < Math.floor(review.rating) ? "★" : "☆"}</span>
            ))}
          </div>
        </div>
        <div className="ml-auto bg-white border px-2 py-1 rounded text-xs font-bold text-gray-700">
          {review.rating}
        </div>
      </div>

      {/* Comment */}
      <div className="flex-grow">
         {/* line-clamp-3 limits text to 3 lines, adding ... if it's too long */}
        <p className="text-gray-600 text-sm leading-relaxed line-clamp-3" title={review.comment}>
          "{review.comment}"
        </p>
      </div>
    </motion.div>
  );
};

export default Reviews;